"use client";

import { useCookieConsent } from "@/components/cookie-consent/use-cookie-consent";
import { useBrowserClient } from "@/utils/supabase/client";
import dynamic from "next/dynamic";
import { usePathname, useSearchParams } from "next/navigation";
import Script from "next/script";
import { Suspense, useContext, useEffect, useState } from "react";
import { type GA4Tracking, createScriptHandlers, setGA4Disabled } from "./ga4";
import { TrackingContext, matchesExcludedPath } from "./use-tracking";

// 最後に送信したpage_viewのパス。
// - 同一パス(+クエリ)への重複送信を防ぐためのガード
// - Suspense境界内のPageViewTrackerはsuspend→resumeでremountされうる
// - StrictMode dev時には親コンポーネント自体が二重mountされ、useRef/useStateの値が
//   リセットされてしまうため、重複防止を確実にするにはモジュールスコープで保持する必要がある
let lastPageViewPath: string | null = null;

/**
 * テスト専用のexport。
 * lastPageViewPath はStrictMode下での二重mountに耐えるためモジュールスコープに置いている。
 * テストケース間でも持ち越されてしまうため、beforeEachで resetState() を呼んでリセットする。
 *
 * @knipignore
 */
export const forTest = {
  resetState: () => {
    lastPageViewPath = null;
  },
};

/**
 * useSearchParams()を利用するため、Next.js App Routerの仕様上Suspense境界内で動作させる。
 *
 * ページビュー送信（GA4 Consent Mode advancedを前提とし、consent状態に関わらず送信する）
 * - consent=denied/null時はsetConsentDefault("denied")の効果でGA4側がcookieless pingとして処理
 * - user_idが設定される前のpageviewを防ぐため authLoaded は待つ
 * - 同一パス(+クエリ)への重複送信を防ぐためモジュールスコープの lastPageViewPath でガード
 * - 計測しない画面（isExcludedPath）では送信しない
 */
function PageViewTracker({
  tracking,
  authLoaded,
  isExcludedPath,
}: {
  tracking: GA4Tracking;
  authLoaded: boolean;
  isExcludedPath: boolean;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!authLoaded) {
      return;
    }
    if (isExcludedPath) {
      // 計測しない画面から元の画面へ戻ったときに、重複とみなされず page_view を送るため
      lastPageViewPath = null;
      return;
    }
    const search = searchParams?.toString() ?? "";
    const targetPath = search ? `${pathname}?${search}` : pathname;
    if (lastPageViewPath === targetPath) {
      return;
    }
    lastPageViewPath = targetPath;
    void tracking.trackPageView(targetPath);
  }, [authLoaded, isExcludedPath, pathname, searchParams, tracking]);

  return null;
}

/**
 * GA4計測に関わる副作用を集約するコンポーネント。
 *
 * 担当する副作用:
 * 1. gtag.js <Script> の読み込み
 * 2. Supabase auth のsubscribe（→ authUserId/authLoaded）
 * 3. consent → GA4 の `updateConsent` 同期
 * 4. (consent, authUserId) → GA4 の `setUserId`/`clearUserId` 同期
 * 5. SPA遷移での `page_view` 送信（PageViewTrackerに委譲）
 * 6. 計測しない画面（Context の excludedPathPattern に一致するパス）での GA4 への送信の停止
 *
 * useEffectはすべて「外部システム（Supabase auth購読 / GA4のグローバル計測状態）との同期」に
 * 限定している。consent と authUserId の2つの外部入力は役割直交な effect
 * （updateConsent用 / user_id同期用）に分離し、片方の変化がもう片方のハンドラを
 * 走らせる「計算の連鎖」を避ける。
 *
 * Context が null（計測無効）なら何もしない。
 */
function GA4TrackerImpl() {
  const { tracking, measurementId, excludedPathPattern } =
    useContext(TrackingContext) ?? {};
  const supabase = useBrowserClient();
  const { consent } = useCookieConsent();
  const [authLoaded, setAuthLoaded] = useState(false);
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const pathname = usePathname();
  const isExcludedPath = matchesExcludedPath(
    excludedPathPattern ?? null,
    pathname,
  );

  // Effect 1: Supabase auth（外部ストア）のsubscribe。
  // callback内ではstate更新のみを行い、GA4側への同期はEffect 2a/2bに任せる。
  useEffect(() => {
    if (!tracking) {
      return;
    }
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthUserId(session?.user?.id ?? null);
      setAuthLoaded(true);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [supabase, tracking]);

  // Effect 2a: consent確定 → GA4のconsent状態を同期。
  // 未確定(null)の間は Consent Mode デフォルト(denied)に委ねる。
  useEffect(() => {
    if (!tracking || consent === null) {
      return;
    }
    void tracking.updateConsent(consent);
  }, [consent, tracking]);

  // Effect 2b: (consent, authUserId) → GA4のuser_idを同期。
  // - granted かつ userIdあり: setUserId
  // - それ以外: clearUserId
  // authLoaded待機により、未初期化のauthUserId=nullでclearUserIdを誤射しないようにする。
  useEffect(() => {
    if (!tracking || consent === null || !authLoaded) {
      return;
    }
    if (consent === "granted" && authUserId !== null) {
      void tracking.setUserId(authUserId);
    } else {
      void tracking.clearUserId();
    }
  }, [consent, authUserId, authLoaded, tracking]);

  // Effect 3: 計測しない画面にいる間は GA4 への送信を止める。
  // 手動の page_view を送らないだけでは、GA4 の拡張計測が自動で送る分を止められないため。
  // ただし画面内の移動ではブラウザの URL が変わった後にこの effect が走るので、移った瞬間に
  // 拡張計測が送るページビューは止められない。GA4 側で「ブラウザの履歴イベントに基づくページの変更」を無効にして補う
  useEffect(() => {
    if (!measurementId) {
      return;
    }
    setGA4Disabled(measurementId, isExcludedPath);
  }, [measurementId, isExcludedPath]);

  if (!tracking || !measurementId) {
    return null;
  }

  const { onLoad, onError } = createScriptHandlers(measurementId);
  return (
    <>
      {/* 計測しない画面では gtag.js を読み込まない（メールのリンクから最初に開く受検・結果の画面で
          gtag.js 自体を読み込ませないため）。計測する画面へ移ったときに読み込む。
          一度読み込んだ後に計測しない画面へ移った場合は、Effect 3 の停止フラグで送信を止める */}
      {!isExcludedPath && (
        <Script
          id="ga4-gtag-js"
          src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
          strategy="afterInteractive"
          onLoad={onLoad}
          onError={onError}
        />
      )}
      {/* useSearchParams()はSuspense境界内でのみ動作するためSuspenseでラップする */}
      <Suspense fallback={null}>
        <PageViewTracker
          tracking={tracking}
          authLoaded={authLoaded}
          isExcludedPath={isExcludedPath}
        />
      </Suspense>
    </>
  );
}

// GA4TrackerはTrackingContextのクライアント専用値に依存しており、SSR時とクライアントで
// 出力が食い違ってhydration mismatchになる。計測の副作用とscript読み込みのみでSSRする
// 意味もないため、next/dynamicでCSR専用に読み込む。
export const GA4Tracker = dynamic(async () => GA4TrackerImpl, {
  ssr: false,
});
